import type { CurriculumOverview, ModuleWithProgress, TopicWithStatus } from '@/lib/api/lms';
import { pickResumeTarget } from '../resumeTarget';

export type ModuleState = 'completed' | 'in_progress' | 'unlocked' | 'locked';
export type TopicState = 'completed' | 'next' | 'open' | 'locked';

export interface AnnotatedTopic { topic: TopicWithStatus; state: TopicState }
export interface AnnotatedModule {
  module: ModuleWithProgress;
  state: ModuleState;
  pct: number;
  topics: AnnotatedTopic[];
}
export interface LearnResume { module: ModuleWithProgress; topic: TopicWithStatus; pct: number }
export interface LearnModel {
  modules: AnnotatedModule[];
  resume: LearnResume | null;
  completedModules: number;
  totalModules: number;
  overallPct: number;
}

function pct(done: number, total: number): number {
  return total > 0 ? Math.round((done / total) * 100) : 0;
}
function isComplete(m: ModuleWithProgress): boolean {
  return m.topic_count > 0 && m.completed_count === m.topic_count;
}

export function buildLearnModel(overview: CurriculumOverview): LearnModel {
  const modules = [...(overview.modules ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const target = pickResumeTarget(modules);
  const resumeModuleIndex = target ? modules.findIndex((m) => m.id === target.moduleId) : -1;

  const annotated: AnnotatedModule[] = modules.map((m, i) => {
    // A module unlocks only once the previous module is fully complete.
    const prevComplete = i === 0 || isComplete(modules[i - 1]);
    let state: ModuleState;
    if (!prevComplete) state = 'locked';
    else if (isComplete(m)) state = 'completed';
    else if (m.completed_count > 0) state = 'in_progress';
    else state = 'unlocked';

    const sorted = [...m.topics].sort((a, b) => a.sort_order - b.sort_order);
    const resumePos =
      i === resumeModuleIndex && target ? sorted.findIndex((t) => t.id === target.topicId) : -1;

    // Topics are never individually locked inside an unlocked module — only the
    // module gate applies. (Topics of a locked module are unreachable anyway.)
    const topics: AnnotatedTopic[] = sorted.map((t, p) => {
      let ts: TopicState;
      if (state === 'locked') ts = 'locked';
      else if (t.status === 'completed') ts = 'completed';
      else if (i === resumeModuleIndex && p === resumePos) ts = 'next';
      else ts = 'open';
      return { topic: t, state: ts };
    });

    return { module: m, state, pct: pct(m.completed_count, m.topic_count), topics };
  });

  let resume: LearnResume | null = null;
  if (target && resumeModuleIndex >= 0) {
    const m = modules[resumeModuleIndex];
    const t = m.topics.find((x) => x.id === target.topicId);
    if (t) resume = { module: m, topic: t, pct: pct(m.completed_count, m.topic_count) };
  }

  return {
    modules: annotated,
    resume,
    completedModules: modules.filter(isComplete).length,
    totalModules: modules.length,
    overallPct: pct(overview.completed_topics, overview.total_topics),
  };
}
